import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { setChecklist, setConditionsAcceptedLocal, setView } from './onboardingSlice';
import { acceptMerchantConditions } from './checklistApi';
import {
  buildMerchantTermSections,
  MERCHANT_TERMS_INTRO,
  MERCHANT_TERMS_TITLE,
} from './merchantTerms';
import { setMerchantConditionsAcceptedLocal } from '../../api/client';

const MerchantConditions: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { applicationId, brandConfig, checklist, conditionsAcceptedLocal } = useSelector((s: RootState) => s.onboarding);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [reviewed, setReviewed] = useState(false);
  const serverDone = checklist?.merchant_conditions_status === 'COMPLETED';
  const already = serverDone || conditionsAcceptedLocal;
  const terms = brandConfig?.register_authorize_requirements?.acceptTerms;
  const title = terms?.title || MERCHANT_TERMS_TITLE;
  const acceptLabel = terms?.text || 'I have reviewed and accept the Merchant Conditions.';
  const sections = useMemo(
    () => buildMerchantTermSections(brandConfig?.merchant_pricing),
    [brandConfig?.merchant_pricing],
  );

  const accept = async () => {
    setBusy(true);
    setError('');
    try {
      // No application yet: client-only acceptance for this session.
      if (!applicationId) {
        setMerchantConditionsAcceptedLocal(true);
        dispatch(setConditionsAcceptedLocal(true));
        dispatch(setView('hub'));
        return;
      }
      const row = await acceptMerchantConditions(applicationId);
      setMerchantConditionsAcceptedLocal(true);
      dispatch(setConditionsAcceptedLocal(true));
      dispatch(setChecklist(row));
      dispatch(setView('hub'));
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Could not record acceptance');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      <Button variant="text" onClick={() => dispatch(setView('hub'))} sx={{ mb: 1, px: 0 }}>← Back to overview</Button>
      <Typography sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, fontSize: 28, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 2 }}>
        {MERCHANT_TERMS_INTRO}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 2,
          mb: 2,
          maxHeight: { xs: 420, md: 520 },
          overflow: 'auto',
          bgcolor: '#f7f4ee',
          px: { xs: 2, md: 3 },
          py: 2.5,
        }}
      >
        {sections.map((section, idx) => (
          <Box key={`${section.title}-${idx}`} sx={{ mb: 2.5, '&:last-child': { mb: 0 } }}>
            <Typography
              sx={{
                fontFamily: '"Fraunces", Georgia, serif',
                fontWeight: 700,
                fontSize: section.major ? 22 : 18,
                color: '#003047',
                textAlign: section.center ? 'center' : 'left',
                mb: (section.items?.length || section.paragraphs?.length) ? 0.75 : 0.25,
                mt: section.major && idx > 0 ? 1.5 : 0,
              }}
            >
              {section.title}
            </Typography>
            {section.paragraphs?.map((p) => (
              <Typography key={p} sx={{ color: '#12232b', fontSize: 15, lineHeight: 1.6, mb: 1 }}>
                {p}
              </Typography>
            ))}
            {section.items?.map((item) => (
              <Typography key={`${section.title}-${item.label}`} sx={{ color: '#12232b', fontSize: 15, lineHeight: 1.6, mb: 0.75 }}>
                <Box component="span" sx={{ fontWeight: 700 }}>{item.label} : </Box>
                {item.text}
              </Typography>
            ))}
          </Box>
        ))}
      </Box>

      {already ? (
        <Alert severity="success">
          {serverDone
            ? 'You have already accepted these conditions.'
            : 'Accepted for this session. They will be saved permanently when you submit the onboarding form.'}
        </Alert>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2, width: '100%' }}>
          <FormControlLabel
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              m: 0,
              width: '100%',
              '& .MuiFormControlLabel-label': { display: 'block', whiteSpace: 'normal' },
            }}
            control={(
              <Checkbox
                checked={reviewed}
                onChange={(e) => setReviewed(e.target.checked)}
                sx={{ mt: -0.25, mr: 1 }}
              />
            )}
            label={acceptLabel}
          />
          <Button variant="contained" onClick={accept} disabled={busy || !reviewed} sx={{ alignSelf: 'flex-start' }}>
            {busy ? 'Saving…' : 'Accept and continue'}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default MerchantConditions;
